import { AgAuthenticationService } from '@ag/vc.ag-core/authentication';
import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { AgWebtemplateConfigurationService } from '@ag/vc.ag-ui';
import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, OnInit, Output, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { CustomerService } from 'src/app/modules/customer-zone/services/customer-service.service';
import { Context } from '../../../core/models/context';
import { Session } from '../../../core/models/session';
import { SessionService } from '../../../core/services/session.service';
import { UserSimulationService } from '../../../modules/simulation/services/user-simulation.service';
import { Brand } from '../../models/brand';
import { DistributionChannel } from '../../models/distributionChannelType';
import { Icon } from '../../models/icon';
import { ProductCodeRange } from '../../models/LeadProducts';
import { BroadcastScreenType, NotificationsResponseModel, ScreenType, ScreenTypeMap } from '../../models/notifications';
import { StartParameter } from '../../models/StartParameter';
import { SurveyParams } from '../../models/survey-params';
import { RouteNames } from '../../routing/app-routing.constants';
import { HelperService } from '../../services/helper.service';
import { ModalComponent } from '../modal/modal.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'custom-layout',
  templateUrl: './layout.component.html',
  styleUrls: [
    './layout.component.scss'
  ],
  encapsulation: ViewEncapsulation.None
})
export class LayoutComponent implements OnInit {
  protected get currentLanguage(): string {
    return this.translateService.currentLang
  }

  wrapperClass: string;
  @Output() languageSelected = new EventEmitter<string>();
  isAuthenticated = false;

  currentBrand: Brand; // Defines which Brand (logo on left) to use
  coBrandImage: string; // Defines which Co-brand (logo on right) to use
  brokerLogo: string;
  brokerWebsiteUrl: string;
  partnerUrlFr: string;
  partnerUrlNl: string;

  Brand = Brand; // Expose enum to html
  RouteNames = RouteNames; // Expose enum to html

  isLoading: boolean; // based on this flag loader will be shown
  isLogoClickable = true;
  isWidgetVisible: boolean;
  inCustomerZone = false;
  private subscription: Subscription = new Subscription();
  oneTrustUrl: string = AgAppConfigService.get<string>('oneTrustSourceUrl');
  oneTrustDomainScript: string;
  showDefaultIcon = true;
  showBanner = false;
  broadcastContent: NotificationsResponseModel;
  currentRoute = '';
  private isPopupClosing = false;
  BroadcastScreenType = BroadcastScreenType;
  broadcastBodyText: SafeHtml;
  Icon = Icon;
  showPromoBanner = false;
  currentScreenType: BroadcastScreenType;
  @ViewChild('broadcastModal') broadcastModal!: ModalComponent;
  constructor(
    public readonly configService: AgWebtemplateConfigurationService,
    /*private timeOutService: TimeOutService,*/
    private readonly sessionService: SessionService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private readonly authenticationService: AgAuthenticationService,
    private readonly userSimulation: UserSimulationService,
    private readonly translateService: AgTranslateService,
    private readonly renderer2: Renderer2,
    private helperService: HelperService,
    private customerService: CustomerService,
    private readonly sanitizer: DomSanitizer) {
    this.processContext(this.sessionService.getContext());
    this.sessionService.context.subscribe((context: Context) => {
      this.processContext(context);
    });
  }

  ngOnInit(): void {
    if (this.configService.configuration.useFullWidth) {
      this.wrapperClass = 'wrapper-fluid';
    } else {
      this.wrapperClass = 'wrapper';
    }
    // if (this.configService.configuration.isTimeOutActive) {
    //     this.timeOutService.initTimeOut(this.configService.configuration.timeout);
    // }
    this.sessionService.getHelpWidgetSubject().subscribe(flag => {
      setTimeout(() => {
        this.isWidgetVisible = flag;
      });
    });
    this.sessionService.getUserAuthenticationStatus().subscribe(authenticationFlag => {
      this.isAuthenticated = authenticationFlag;
    });
    this.router.events.pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter((event: any) => event instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      if (e.url.includes(`/${RouteNames.customerZone}`)) {
        this.inCustomerZone = true;
        this.showDefaultIcon = (e.url.includes(`/${RouteNames.iContinue}`)) ? false : true;
      } else {
        this.inCustomerZone = false;
        this.showDefaultIcon = false;
      }
      this.setFavicon();

      this.currentRoute = e.urlAfterRedirects;
      this.currentScreenType = this.getCurrentScreenType(this.currentRoute);
      const currentScreen = this.getCurrentScreenType(this.currentRoute);
      const uci = localStorage.getItem('uci');
      let screenType: ScreenType;
      if (
        currentScreen === BroadcastScreenType.Dashboard ||
        currentScreen === BroadcastScreenType.ContractDetails ||
        currentScreen === BroadcastScreenType.Profile
      ) {
        screenType = ScreenType.CZ;
      } else if (currentScreen === BroadcastScreenType.Recap) {
        screenType = ScreenType.Recap;
      }
      if (currentScreen) {
        if (uci) {
          this.customerService.fetchNotificationMessages(uci, screenType).pipe(
            switchMap(() =>
              this.customerService.getBroadcastData()), take(1))
            .subscribe(data => {
              this.broadcastContent = data;
              this.checkBroadcast();
            })
        }
      } else {
        this.checkBroadcast();
      }
    });

    if (this.authenticationService.isAuthenticated) {
      if (!this.sessionService.getUserParams()?.distributionChannel || !this.sessionService.getContext()?.brand) {
        const userParams = this.sessionService.getUserParams();
        userParams.distributionChannel = DistributionChannel.AG;
        const usersurvey = JSON.parse(atob(sessionStorage.getItem('userSurvey'))) as SurveyParams;
        // last used partner incase user has travelled to their pending survey of their respective partner
        userParams.partnercode = sessionStorage.userSurvey && Number(usersurvey?.partnerId);
        this.sessionService.setModifiedUserParam(userParams);
        if (this.sessionService.getUserParams().partnercode) {
          this.userSimulation.getStartParametersForValidation().pipe(map(x => x.Result)).subscribe((d: StartParameter) => {
            if (d) {
              this.sessionService.setHeaderContext(null, null, null, d.PartnerLogo, d.PartnerLinkFR, d.PartnerLinkNL);
            }
          });
        } else { // incase the session variables are expired after prolonged stay, this is the basic header layout for logged in user
          this.sessionService.setContext(Brand.Uppie, '');
        }
      }
    }
    if (this.sessionService?.getUserParams()?.distributionChannel === DistributionChannel.Broker) {
      this.oneTrustDomainScript = AgAppConfigService.get<string>('brokerOneTrustDomainScript');
    } else {
      this.oneTrustDomainScript = AgAppConfigService.get<string>('oneTrustDomainScript');
    }
    setTimeout(() => {
      this.oneTrustDomainScript && this.appendCookieScript(this.oneTrustDomainScript);
    }, 500);
    this.sessionService.hasParamRefreshed().subscribe(res => {
      if (res) {
        this.setFavicon();
        this.oneTrustDomainScript = this.sessionService.getUserParams().distributionChannel == DistributionChannel.Broker
          ? AgAppConfigService.get<string>('brokerOneTrustDomainScript')
          : AgAppConfigService.get<string>('oneTrustDomainScript');
        this.appendCookieScript(this.oneTrustDomainScript);
      }
    });
    /*this.subscription.add(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        window.location.reload();
      })
    );*/
  }


  getCurrentScreenType(currentUrl: string): BroadcastScreenType | null {
    for (const [type, paths] of Object.entries(ScreenTypeMap)) {
      if (paths.some(p => currentUrl.includes(p))) {
        return type as BroadcastScreenType;
      }
    }
    return null;
  }

  checkBroadcast(): void {
    this.showPromoBanner = false;
    const data = this.broadcastContent;
    if (!data) return;

    if (data.IsBroadCastMessageActive) {
      this.showPromoBanner = false;
      const currentScreen = this.getCurrentScreenType(this.currentRoute);
      const isPopupApplicable = data.IsBroadCastMessageActive && (
        (data.ScreenType === ScreenType.Both && (currentScreen === BroadcastScreenType.Dashboard || currentScreen === BroadcastScreenType.Recap)) ||
        (data.ScreenType === ScreenType.CZ && currentScreen === BroadcastScreenType.Dashboard) ||
        (data.ScreenType === ScreenType.Recap && currentScreen === BroadcastScreenType.Recap)
      );
      if (isPopupApplicable && !this.helperService.isPoupShown(this.currentRoute)) {
        setTimeout(() => {
          if (this.broadcastModal) {
            this.broadcastModal.openModal();
          }
        });
        this.helperService.markPopupShown(this.currentRoute);
        this.showBanner = false;
      } else if (
        currentScreen === BroadcastScreenType.ContractDetails ||
        currentScreen === BroadcastScreenType.Profile ||
        (isPopupApplicable && this.helperService.isPoupShown(this.currentRoute))) {
        this.showBanner = true;
        this.helperService.setPopupHandledStatus();
      } else {
        this.showBanner = false;
        this.helperService.setPopupHandledStatus();
      }
    }
    else if (!data.IsBroadCastMessageActive && data.HasActivePromoMessage) {
      this.broadcastModal?.closeModal();
      this.showBanner = false;
      this.helperService.clearAllPopupShownFlags();
      this.helperService.setPopupHandledStatus();
      this.broadcastBodyText = this.translateService.currentLang === 'nl' ? this.sanitizer.bypassSecurityTrustHtml(this.broadcastContent.BodyNL) : this.sanitizer.bypassSecurityTrustHtml(this.broadcastContent.BodyFR);
      this.translateService.onLangChange.subscribe(() => {
        if (data.HasActivePromoMessage)
          this.broadcastBodyText = this.translateService.currentLang === 'nl' ? this.sanitizer.bypassSecurityTrustHtml(this.broadcastContent.BodyNL) : this.sanitizer.bypassSecurityTrustHtml(this.broadcastContent.BodyFR);
      });
      const currentScreen = this.getCurrentScreenType(this.currentRoute);
      if (
        currentScreen === BroadcastScreenType.ContractDetails ||
        currentScreen === BroadcastScreenType.Profile) {
        this.showPromoBanner = true;
      }
      else {
        this.showPromoBanner = false;
      }
    }
    else if (!data.IsBroadCastMessageActive && !data.HasActivePromoMessage) {
      this.broadcastModal?.closeModal();
      this.showBanner = false;
      this.showPromoBanner = false;
      this.helperService.clearAllPopupShownFlags();
      this.helperService.setPopupHandledStatus();
      return;
    }

  }

  onClosePopup(): void {
    if (this.isPopupClosing) return;  // Prevent infinite loops
    this.isPopupClosing = true;       // Set flag to true to indicate closing process has started
    this.broadcastModal?.closeModal();
    this.showBanner = true;

    const currentScreen = this.getCurrentScreenType(this.currentRoute);
    if (currentScreen === BroadcastScreenType.Dashboard || currentScreen === BroadcastScreenType.Recap) {
      this.helperService.markPopupShown(this.currentRoute);
      this.helperService.setPopupHandledStatus();
    }

    this.isPopupClosing = false;
  }

  switchLanguage(language: string): void {
    this.sessionService.setUserLanguage(language);
    this.languageSelected.emit(language);
    if (localStorage.personId) {
      const reqObj = {
        PersonId: Number(localStorage.personId),
        UserLanguage: language
      }
      this.customerService.saveUserLanguage(reqObj).subscribe();
    }
    // below reload is done so that broker=search-tool, which is a react tool, can be loaded correctly on every language change
    if (this.sessionService.getUserParams().distributionChannel === DistributionChannel.Broker) {
      window.location.reload();
    }
    setTimeout(() => {
      this.appendCookieScript(this.oneTrustDomainScript);
    }, 500);
  }

  private processContext(context: Context) {
    this.currentBrand = context.brand;
    this.coBrandImage = context.coBrandLogo;
    this.brokerLogo = context.brokerLogo;
    this.brokerWebsiteUrl = context.brokerWebsiteUrl;
    this.isLogoClickable = context.isBrandClickable;
    this.partnerUrlFr = context.partnerUrlFr;
    this.partnerUrlNl = context.partnerUrlNl;
  }

  GoToHome(): void {
    if (this.isLogoClickable) {
      // home page is accessible only when user is logged in and not in the mandatory email vaerification screen
      if ((this.isAuthenticated || this.authenticationService.isAuthenticated || this.authenticationService.accessToken)) {
        const routesToCheck = [
          RouteNames.RedirectionScreenGeneric,
          RouteNames.BlockedAge,
          RouteNames.BlockedAgeRangeYongo,
          RouteNames.BlockedCountry,
          RouteNames.BlockedCountryRangeYongo,
          RouteNames.BlockedExpiredIdentification,
          RouteNames.BlockUserNissUCIExists
        ];

        if (routesToCheck.some(route => window.location.pathname.includes(route))) {
          const yongoPublicWebsite = 'https://yongo.be/';
          if (this.translateService.currentLang === 'nl') {
            window.location.href = yongoPublicWebsite + 'nl'
          }
          else {
            window.location.href = yongoPublicWebsite + 'fr'
          }
        }
        else if (window.location.pathname.includes(RouteNames.RedirectionScreenChild)) {
          if (this.translateService.currentLang === 'nl') {
            window.location.href = 'https://www.aginsurance.be/retail/nl/paginas/homepage.aspx'
          }
          else {
            window.location.href = 'https://www.aginsurance.be/Retail/fr/Pages/homepage.aspx'
          }
        }
        else {
          void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
        }
      } else {// login page or start of simulation based on the current screen
        if (window.location.pathname.includes(RouteNames.login)) {

          void this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.login]);

        } else {
          void this.router.navigate(['/' + RouteNames.simulation]);
        }
      }
    }
  }

  appendCookieScript(scriptId: string): void {
    this.clearCookieScript();
    if (this.translateService.currentLang) {
      const cookieScript = this.renderer2.createElement('script') as HTMLScriptElement;
      this.renderer2.setAttribute(cookieScript, 'type', 'text/javascript');
      this.renderer2.setAttribute(cookieScript, 'src', this.oneTrustUrl);
      this.renderer2.setAttribute(cookieScript, 'charset', 'UTF-8');
      this.renderer2.setAttribute(cookieScript, 'charset', 'UTF-8');
      this.renderer2.setAttribute(cookieScript, 'data-domain-script', scriptId);
      this.renderer2.setAttribute(cookieScript, 'data-document-language', 'true');
      if (this.translateService.currentLang === 'nl') {
        this.renderer2.setAttribute(cookieScript, 'data-language', 'nl-BE');
      }
      else {
        this.renderer2.setAttribute(cookieScript, 'data-language', 'fr-BE');
      }

      const optanonWrapper = this.renderer2.createElement('script') as HTMLScriptElement;
      this.renderer2.setAttribute(optanonWrapper, 'type', 'text/javascript');
      this.renderer2.setAttribute(optanonWrapper, 'id', 'cookie-callback');
      this.renderer2.setProperty(optanonWrapper, 'innerHTML', 'function OptanonWrapper() { }');

      this.renderer2.insertBefore(this.document.head, optanonWrapper, this.document.head.firstChild);
      this.renderer2.insertBefore(this.document.head, cookieScript, optanonWrapper);
    }
  }

  clearCookieScript(): void {
    const scripts = document.querySelectorAll("script[src*='" + this.oneTrustUrl + "']");
    document.getElementById('onetrust-consent-sdk')?.remove();
    this.document.getElementById('cookie-callback')?.remove();
    scripts.forEach((script) => {
      script.parentNode.removeChild(script);
    });
  }

  private setFavicon() {
    let favicon = 'favicon.ico';
    let favicon16 = 'favicon-ag-16p.png';
    let favicon32 = 'favicon-ag-32p.png';
    let favicon57 = 'favicon-ag-57p.png';
    let favicon60 = 'favicon-ag-60p.png';
    let favicon72 = 'favicon-ag-72p.png';
    let favicon76 = 'favicon-ag-76p.png';
    let favicon96 = 'favicon-ag-96p.png';
    let favicon114 = 'favicon-ag-114p.png';
    let favicon120 = 'favicon-ag-120p.png';
    let favicon144 = 'favicon-ag-144p.png';
    let favicon152 = 'favicon-ag-152p.png';
    let favicon180 = 'favicon-ag-180p.png';
    let favicon192 = 'favicon-ag-192p.png';
    let themeColor = '#99cc00';

    // TODO replace favicon_uppie.ico with correct version
    const usersession = JSON.parse(localStorage.getItem('userSession')) as Session;
    if (usersession?.userParam?.partnercodeRange == ProductCodeRange.Uppie && !this.showDefaultIcon) {
      favicon = 'favicon_uppie.ico';
      favicon16 = 'favicon-uppie-16p.png';
      favicon32 = 'favicon-uppie-32p.png';
      favicon57 = 'favicon-uppie-57p.png';
      favicon60 = 'favicon-uppie-60p.png';
      favicon72 = 'favicon-uppie-72p.png';
      favicon76 = 'favicon-uppie-76p.png';
      favicon96 = 'favicon-uppie-96p.png';
      favicon114 = 'favicon-uppie-114p.png';
      favicon120 = 'favicon-uppie-120p.png';
      favicon144 = 'favicon-uppie-144p.png';
      favicon152 = 'favicon-uppie-152p.png';
      favicon180 = 'favicon-uppie-180p.png';
      favicon192 = 'favicon-uppie-192p.png';
      themeColor = '#00c1fc';
    } else if ([ProductCodeRange.Yongo, ProductCodeRange.Yongo1].includes(usersession?.userParam?.partnercodeRange) && !this.showDefaultIcon) {
      favicon = 'favicon_yongo.ico';
      favicon16 = 'favicon-yongo-16p.png';
      favicon32 = 'favicon-yongo-32p.png';
      favicon57 = 'favicon-yongo-57p.png';
      favicon60 = 'favicon-yongo-60p.png';
      favicon72 = 'favicon-yongo-72p.png';
      favicon76 = 'favicon-yongo-76p.png';
      favicon96 = 'favicon-yongo-96p.png';
      favicon114 = 'favicon-yongo-114p.png';
      favicon120 = 'favicon-yongo-120p.png';
      favicon144 = 'favicon-yongo-144p.png';
      favicon152 = 'favicon-yongo-152p.png';
      favicon180 = 'favicon-yongo-180p.png';
      favicon192 = 'favicon-yongo-192p.png';

      themeColor = '#00c1fc';
    }

    if (this.document.getElementById('appFaviconMain')) {
      this.document.getElementById('appFaviconMain').setAttribute('href', favicon);
    }
    if (this.document.getElementById('appFavicon16')) {
      this.document.getElementById('appFavicon16').setAttribute('href', favicon16);
    }
    if (this.document.getElementById('appFavicon32')) {
      this.document.getElementById('appFavicon32').setAttribute('href', favicon32);
    }
    if (this.document.getElementById('appFavicon57')) {
      this.document.getElementById('appFavicon57').setAttribute('href', favicon57);
    }
    if (this.document.getElementById('appFavicon60')) {
      this.document.getElementById('appFavicon60').setAttribute('href', favicon60);
    }
    if (this.document.getElementById('appFavicon72')) {
      this.document.getElementById('appFavicon72').setAttribute('href', favicon72);
    }
    if (this.document.getElementById('appFavicon76')) {
      this.document.getElementById('appFavicon76').setAttribute('href', favicon76);
    }
    if (this.document.getElementById('appFavicon96')) {
      this.document.getElementById('appFavicon96').setAttribute('href', favicon96);
    }
    if (this.document.getElementById('appFavicon114')) {
      this.document.getElementById('appFavicon114').setAttribute('href', favicon114);
    }
    if (this.document.getElementById('appFavicon120')) {
      this.document.getElementById('appFavicon120').setAttribute('href', favicon120);
    }
    if (this.document.getElementById('appFavicon144')) {
      this.document.getElementById('appFavicon144').setAttribute('href', favicon144);
    }
    if (this.document.getElementById('appFavicon152')) {
      this.document.getElementById('appFavicon152').setAttribute('href', favicon152);
    }
    if (this.document.getElementById('appFavicon180')) {
      this.document.getElementById('appFavicon180').setAttribute('href', favicon180);
    }
    if (this.document.getElementById('appFavicon192')) {
      this.document.getElementById('appFavicon192').setAttribute('href', favicon192);
    }
    if (this.document.getElementById('themecolor')) {
      this.document.getElementById('themecolor').setAttribute('content', themeColor);
    }
  }
}
